import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import _ from 'lodash'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import BudgetsTable from 'src/components/BudgetsTable/BudgetsTable'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import FilterSection from 'src/components/FilterSection/FilterSection'
import NavAwayBox, { NavAwayProps } from 'src/components/NavAwayBox/NavAwayBox'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import {
  useDebounced,
  useQueryParamArrayHandler,
  useStringSetState,
  useWindowScrollBottomHandler,
} from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import {
  CreditCardListModel,
  CreditCardStatus,
} from 'src/models/creditCard.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { InfiniteScrollProps } from 'src/types'
import { setEquality } from 'src/utils/set.utils'
import { css } from 'styled-components'

const statusCheckboxRowCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
`

export interface BudgetsFiltersBody {
  search?: string
  statuses?: CreditCardStatus[]
  users?: string[]
  owners?: string[]
}

export interface BudgetsFilters {
  search?: string
  statuses: Set<CreditCardStatus>
  users: Set<string>
  owners: Set<string>
}

export interface BudgetsAnalyticsEventProperties {
  search?: string
  status?: string[]
  budget_user?: string[]
  budget_creator?: string[]
}

interface BudgetsBoxWithFiltersProps {
  className?: string
  budgets: CreditCardListModel[]
  isSlim: boolean
  defaultTitle: React.ReactElement
  isSnapshot?: boolean
  navAway?: NavAwayProps
  readOnly?: boolean
  initialFilterValues?: Partial<BudgetsFiltersBody>
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  recipientOptions?: UserOption[]
  totalBudgets: number
  setSelectedFilters?: (
    selectedFilters: BudgetsAnalyticsEventProperties,
  ) => void
  clientHeight?: number
  infiniteScrollProps: InfiniteScrollProps<BudgetsFiltersBody>
}

const BudgetsBoxWithFilters: React.FC<BudgetsBoxWithFiltersProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const queryParams = useSelector(selectQueryParams)

  const [initialRender, setInitialRender] = useState<boolean>(true)
  const [pushNewSearchState, setPushNewSearchState] = useState<boolean>(false)
  const showNewBudgetsTable = useSelector(
    selectFeatureFlaggerFromState,
  )?.show_new_budgets_table

  const resetFilterValues: BudgetsFilters = useMemo(
    () => ({
      search: '',
      statuses: new Set<CreditCardStatus>(),
      users: new Set<string>(),
      owners: new Set<string>(),
    }),
    [],
  )

  const initialFilterValues: BudgetsFilters = useMemo(
    () => ({
      search: '',
      statuses: new Set<CreditCardStatus>(
        props.initialFilterValues?.statuses
          ? props.initialFilterValues?.statuses
          : [],
      ),
      users: new Set<string>(
        props.initialFilterValues?.users
          ? props.initialFilterValues?.users
          : [],
      ),
      owners: new Set<string>(
        props.initialFilterValues?.owners
          ? props.initialFilterValues?.owners
          : [],
      ),
    }),
    [props.initialFilterValues],
  )

  const [searchFilter, setSearchFilter] = useState(initialFilterValues.search)

  const {
    set: statusSet,
    setSet: setStatusSet,
    handleSelection: handleStatusSelection,
  } = useStringSetState(initialFilterValues.statuses)
  const {
    set: userSet,
    setSet: setUserSet,
    handleSelection: handleUserSelection,
  } = useStringSetState(initialFilterValues?.users ?? new Set<string>())
  const {
    set: ownerSet,
    setSet: setOwnerSet,
    handleSelection: handleOwnersSelection,
  } = useStringSetState(initialFilterValues?.owners ?? new Set<string>())

  const resetSearchFilter = (): void => {
    setSearchFilter(resetFilterValues.search)
  }
  const resetStatusSet = (): void => {
    setStatusSet(resetFilterValues?.statuses ?? new Set<string>())
  }
  const resetUserSet = (): void => {
    setUserSet(resetFilterValues.users ?? new Set<string>())
  }
  const resetOwnerSet = (): void => {
    setOwnerSet(resetFilterValues.owners ?? new Set<string>())
  }

  const onFiltersResetClick = (): void => {
    resetStatusSet()
    resetUserSet()
    resetOwnerSet()
    resetSearchFilter()
    pushUrlStateDebounce()
  }

  const isDirty =
    searchFilter !== resetFilterValues.search ||
    !setEquality(statusSet, resetFilterValues.statuses) ||
    !setEquality(userSet, resetFilterValues.users) ||
    !setEquality(ownerSet, resetFilterValues.owners)

  const filtersAppliedCount = (): number => {
    let appliedCount = 0
    if (!setEquality(statusSet, resetFilterValues.statuses)) {
      appliedCount++
    }
    if (!setEquality(userSet, resetFilterValues.users)) {
      appliedCount++
    }
    if (!setEquality(ownerSet, resetFilterValues.owners)) {
      appliedCount++
    }
    return appliedCount
  }

  const filtersBody: BudgetsFiltersBody = {
    search: searchFilter ? `${searchFilter}` : undefined,
    statuses:
      statusSet.size > 0
        ? (Array.from(statusSet) as CreditCardStatus[])
        : undefined,
    users: userSet ? Array.from(userSet) : undefined,
    owners: ownerSet ? Array.from(ownerSet) : undefined,
  }

  const handleScroll = useCallback((): void => {
    if (
      props.infiniteScrollProps !== undefined &&
      !props.infiniteScrollProps.loading &&
      !props.infiniteScrollProps.complete &&
      props.infiniteScrollProps.page !== undefined &&
      !showNewBudgetsTable
    ) {
      props.infiniteScrollProps.fetch?.(
        {
          ...filtersBody,
          statuses: statusSet.has(CreditCardStatus.Inactive)
            ? [...(filtersBody.statuses ?? []), CreditCardStatus.Expired]
            : filtersBody.statuses,
        },
        props.infiniteScrollProps.page + 1,
      )
    }
  }, [
    props.infiniteScrollProps?.loading,
    props.infiniteScrollProps?.complete,
    props.infiniteScrollProps?.fetch,
    props.infiniteScrollProps?.page,
    showNewBudgetsTable,
  ])
  useWindowScrollBottomHandler(handleScroll)

  const { debouncedF } = useDebounced((): void => {
    props.infiniteScrollProps?.fetch?.(
      {
        ...filtersBody,
        statuses: statusSet.has(CreditCardStatus.Inactive)
          ? [...(filtersBody.statuses ?? []), CreditCardStatus.Expired]
          : filtersBody.statuses,
      },
      0,
    )
  })

  const pushUrlStateDebounce = useCallback(
    _.debounce(() => {
      setPushNewSearchState(true)
    }, 500),
    [],
  )

  const pushUrlState = (): void => {
    const paramString = queryString.stringify(filtersBody)
    window.history.pushState(
      JSON.stringify({}),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  useEffect((): void => {
    if (pushNewSearchState) {
      pushUrlState()
      setPushNewSearchState(false)
    }
  }, [pushNewSearchState])

  useEffect((): void => {
    debouncedF()
    props.setSelectedFilters &&
      props.setSelectedFilters({
        search: searchFilter,
        status: statusSet.size > 0 ? Array.from(statusSet) : undefined,
        budget_user: userSet.size > 0 ? Array.from(userSet) : undefined,
        budget_creator: ownerSet.size > 0 ? Array.from(ownerSet) : undefined,
      })
  }, [searchFilter, statusSet, userSet, ownerSet])

  useEffect((): void => {
    if (initialRender) {
      setInitialRender(false)
    } else {
      setUserSet(new Set(useQueryParamArrayHandler(queryParams.users)))
      setOwnerSet(new Set(useQueryParamArrayHandler(queryParams.owners)))
      setStatusSet(new Set(useQueryParamArrayHandler(queryParams.statuses)))
    }
  }, [queryParams])

  return (
    <NavAwayBox
      navAway={props.isSnapshot ? props.navAway : undefined}
      loading={props.infiniteScrollProps?.loading && !showNewBudgetsTable}
    >
      <FilterSection
        appliedFiltersCount={filtersAppliedCount()}
        topLeftContent={
          <div
            css={css`
              width: 100%;
              display: flex;
              justify-content: space-between;
              column-gap: 40px;
              ${getBreakpointDown(breakpointSizes.XXSM)(css`
                flex-direction: column;
                row-gap: 20px;
              `)}
            `}
          >
            {isDirty && props.infiniteScrollProps ? (
              props.infiniteScrollProps.loading &&
              !props.infiniteScrollProps.complete ? (
                <Processing />
              ) : (
                <span
                  css={css`
                    ${getFontCss(
                      20,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {props.totalBudgets === 1
                    ? props.infiniteScrollProps.totalItems === 1
                      ? translate(
                          '${filteredCount} Result (of ${totalCount} Budget)',
                          {
                            filteredCount: `${props.infiniteScrollProps.totalItems || 0}`,
                            totalCount: `${props.totalBudgets}`,
                          },
                        )
                      : translate(
                          '${filteredCount} Results (of ${totalCount} Budget)',
                          {
                            filteredCount: `${props.infiniteScrollProps.totalItems || 0}`,
                            totalCount: `${props.totalBudgets}`,
                          },
                        )
                    : props.infiniteScrollProps.totalItems === 1
                      ? translate(
                          '${filteredCount} Result (of ${totalCount} Budgets)',
                          {
                            filteredCount: `${props.infiniteScrollProps.totalItems || 0}`,
                            totalCount: `${props.totalBudgets}`,
                          },
                        )
                      : translate(
                          '${filteredCount} Results (of ${totalCount} Budgets)',
                          {
                            filteredCount: `${props.infiniteScrollProps.totalItems || 0}`,
                            totalCount: `${props.totalBudgets}`,
                          },
                        )}
                </span>
              )
            ) : (
              props.defaultTitle
            )}
            {/* <div css={css`
              position: relative;
              flex-grow: 2;
            `}>
              <KeywordFilter
                value={searchFilter}
                onChange={(val: string): void => { setSearchFilter(val) }}
                showLabel={false}
                placeholder={translateSingleString(locale, 'Name, Budget Limit, Remaining Budget, etc.')}
              />
            </div> */}
          </div>
        }
        hideButtonChevron
        filtersFlyoutProps={{
          title: translate('Budget Filters'),
          onReset: onFiltersResetClick,
          loading: props.infiniteScrollProps?.loading,
          filters: (
            <>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 12px;
                `}
              >
                <div
                  css={css`
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {translate('Budget Status')}
                </div>
                <label css={statusCheckboxRowCss}>
                  <Checkbox
                    checked={statusSet.has(CreditCardStatus.Active) || false}
                    onChange={(): void => {
                      handleStatusSelection(CreditCardStatus.Active)
                      pushUrlStateDebounce()
                    }}
                    aria-label={translate('Active')}
                    data-testid="activeCheckbox"
                  />
                  {translate('Active')}
                </label>
                <label css={statusCheckboxRowCss}>
                  <Checkbox
                    checked={statusSet.has(CreditCardStatus.Inactive) || false}
                    onChange={(): void => {
                      handleStatusSelection(CreditCardStatus.Inactive)
                      pushUrlStateDebounce()
                    }}
                    aria-label={translate('Inactive')}
                    data-testid="inactiveCheckbox"
                  />
                  {translate('Inactive')}
                </label>
              </div>
              <DividerLineDiv
                css={css`
                  margin: 20px 0px;
                `}
              />
              <div
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('Budget User')}
              </div>
              <UserDropdown
                css={css`
                  margin-top: 8px;
                `}
                isCreatable={false}
                placeholder={translateSingleString(locale, 'All budget users')}
                chooseOption={(val: string): void => {
                  handleUserSelection(val)
                  pushUrlStateDebounce()
                }}
                selectedValues={userSet}
                optionGroups={[
                  {
                    options: props.recipientOptions ?? [],
                  },
                ]}
                isMulti
                onResetClick={(): void => {
                  resetUserSet()
                  pushUrlStateDebounce()
                }}
                infiniteScrollProps={props.userInfiniteScrollProps}
                minimalDisplay
                data-testid="budgetUserDropdown"
              />
              <DividerLineDiv
                css={css`
                  margin: 20px 0px;
                `}
              />
              <div
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('Budget Creator')}
              </div>
              <UserDropdown
                css={css`
                  margin-top: 8px;
                `}
                isCreatable={false}
                placeholder={translateSingleString(
                  locale,
                  'All budget creators',
                )}
                chooseOption={(val: string): void => {
                  handleOwnersSelection(val)
                  pushUrlStateDebounce()
                }}
                selectedValues={ownerSet}
                optionGroups={[
                  {
                    options: props.recipientOptions ?? [],
                  },
                ]}
                isMulti
                onResetClick={(): void => {
                  resetOwnerSet()
                  pushUrlStateDebounce()
                }}
                infiniteScrollProps={props.userInfiniteScrollProps}
                minimalDisplay
                data-testid="budgetCreatorDropdown"
              />
            </>
          ),
        }}
      />
      <div
        css={css`
          background-color: ${styleConstants.colors.gray_lightest};
          ${!props.isSnapshot &&
          css`
            border-bottom-left-radius: ${styleConstants.borderRadius.box};
            border-bottom-right-radius: ${styleConstants.borderRadius.box};
            overflow: hidden;
          `}
        `}
      >
        {props.budgets.length > 0 && (
          <BudgetsTable
            budgets={props.budgets}
            isSlim={props.isSlim}
            infiniteScrollProps={props.infiniteScrollProps}
            filtersBody={filtersBody}
            clientHeight={props.clientHeight}
          />
        )}
        {!isDirty &&
          props.budgets.length === 0 &&
          props.infiniteScrollProps?.complete && (
            <TableEmptyState
              design={TableEmptyStateDesigns.AccountBudgets}
              readOnly={!!props.readOnly}
            />
          )}
        {isDirty &&
          props.budgets.length === 0 &&
          props.infiniteScrollProps?.complete && (
            <TableEmptyState
              design={TableEmptyStateDesigns.AccountBudgetsFiltered}
              readOnly={!!props.readOnly}
            />
          )}
      </div>
    </NavAwayBox>
  )
}

export default BudgetsBoxWithFilters
